import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { cookieUtils } from "../utils/cookieUtils";
import { ErrorToast } from "../components/toastMessage";
// Routes that are accessible even if the user is not fully registered
const whitelistRoutes = [
  "/account-verification-status",
  "/user-account-not-verified",
  "/launch-page",
  "/user-profile",
];

export function isUserOrArchitect<Props extends JSX.IntrinsicAttributes>(
  Component: React.FC<Props>
): React.FC<Props> {
  const Wrapper: React.FC<Props> = (props) => {
    const location = useLocation();
    const user = useUser();

    // Retrieve user registration details from local storage
    const userRegistrationDetails = JSON.parse(
      localStorage.getItem("userRegistrationDetails") || "{}"
    ) as Record<string, any>;

    // Retrieve user details from secure cookies
    const userDetails = cookieUtils.getSecureCookie("userDetails");

    /**
     * Restrict unverified users from accessing non-whitelisted pages.
     * If a user is registered but not verified, they should only be allowed
     * on specific pages (whitelistRoutes). Otherwise, they are redirected
     * to the account verification page.
     */
    if (
      !whitelistRoutes.includes(location.pathname) && // If current path is NOT whitelisted
      userDetails?.role === "user" && // User role is "user"
      userRegistrationDetails?.regestration_status !== 1 // Registration is not complete
    ) {
      ErrorToast("Access denied");
      return <Navigate to="/user-account-not-verified" replace />;
    }

    /**
     * Ensure only authorized roles can access user-specific routes.
     * If the user's role is not in the allowed list, they are redirected to the login page.
     */
    if (
      !["user", "architect", "system admin", "level 3", "level 2", "level 1"].includes(
        user?.role?.toLowerCase() || ""
      )
    ) {
      return <Navigate to="/onboarding/login" replace />;
    }

    // If the user passes all checks, render the requested component
    return <Component {...props} />;
  };

  return Wrapper;
}
